import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import PermissionNotice from "../../components/PermissionNotice";

export default function DropdownManager() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [roleNames, setRoleNames] = useState({});
  const [serverUsersByRole, setServerUsersByRole] = useState({});
  const [allAdmins, setAllAdmins] = useState([]);
  const [localSelectedIds, setLocalSelectedIds] = useState({});
  const [currentUserIsSuperAdmin, setCurrentUserIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setErr(null);
        setLoading(true);

        const authHeader = {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };

        const [groupRes, adminRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/manager/users-by-role`, {
            headers: authHeader,
          }),
          axios.get(`${BASE_URL}/api/dropdowns/admin`, { headers: authHeader }),
        ]);

        // server: { [roleId]: [{id, name}, ...] }
        const rb = groupRes.data.usersByRole || {};

        // Normalize selected IDs to Number
        const initial = {};
        for (const roleId of Object.keys(rb)) {
          initial[roleId] = (rb[roleId] || []).map((u) => Number(u.id));
        }

        setRoleNames(groupRes.data.roleNames || {});
        setServerUsersByRole(rb);
        setCurrentUserIsSuperAdmin(Boolean(groupRes.data.currentUserIsSuperAdmin));

        // Normalize option values to Number (defensive)
        const adminOptions = (adminRes.data || []).map((o) => ({
          label: o.label,
          value: Number(o.value),
        }));
        setAllAdmins(adminOptions);

        setLocalSelectedIds(initial);
      } catch (e) {
        console.error("Dropdown Manager load error:", e);
        setErr("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [BASE_URL]);

  // Roles to render (combined keys from names + usersByRole)
  const allRoleIds = useMemo(() => {
    const all = new Set([
      ...Object.keys(roleNames),
      ...Object.keys(serverUsersByRole),
    ]);
    return [...all]
      .map(Number)
      .filter((id) => {
        const isSuperAdminRole = id === 17;
        if (isSuperAdminRole && !currentUserIsSuperAdmin) {
          return false; // hide if user not Super Admin
        }
        return true;
      })
      .sort((a, b) => a - b);
  }, [roleNames, serverUsersByRole, currentUserIsSuperAdmin]);

  // Selected option objects for a role
  const valueForRole = (roleId) => {
    const idSet = new Set((localSelectedIds[roleId] || []).map(Number));
    return allAdmins.filter((o) => idSet.has(Number(o.value)));
  };

  const handleChangeRole = (roleId, selectedOpts) => {
    const newIds = (selectedOpts || []).map((o) => Number(o.value));
    setLocalSelectedIds((prev) => ({ ...prev, [roleId]: newIds }));
  };

  const handleReset = () => {
    const initial = {};
    for (const roleId of Object.keys(serverUsersByRole)) {
      initial[roleId] = (serverUsersByRole[roleId] || []).map((u) =>
        Number(u.id)
      );
    }
    setLocalSelectedIds(initial);
  };

  const handleSaveAll = async () => {
    try {
      setSaving(true);
      setErr("");

      const authHeader = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      const reqs = Object.entries(localSelectedIds).map(([roleId, userIds]) =>
        axios.put(
          `${BASE_URL}/api/manager/roles/${roleId}/users`,
          { userIds: userIds.map(Number) },
          { headers: authHeader }
        )
      );

      await Promise.all(reqs);

      alert("✅ List updated successfully.");
    } catch (err) {
      console.error("❌ Error saving list:", err);
      alert("Failed to save tag categories. Please check the console.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-3">Loading...</div>;

  return (
    <div className="form-wrapper my-2">
      <div className="d-flex justify-content-between align-items-center">
        <h2 className="mb-3">Dropdown Manager</h2>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleReset}
            disabled={saving || !currentUserIsSuperAdmin}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveAll}
            disabled={saving || !currentUserIsSuperAdmin}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}
      {!currentUserIsSuperAdmin && (
        <PermissionNotice message="No permission to edit role assignments. Only Super Admin can make changes." compact />
      )}

      <div className="row">
        {allRoleIds.map((roleId) => (
          <div className="col-md-6" key={roleId} style={{ marginBottom: 24 }}>
            <fieldset className="border rounded p-3 bg-white">
              <legend
                className="w-auto"
                style={{ fontSize: 18, fontWeight: 600 }}
              >
                {roleNames[roleId] || `Role ${roleId}`}
              </legend>
              <div className="mb-2" style={{ fontSize: 13, fontWeight: 600 }}>
                Included Users
              </div>
              <Select
                isMulti
                options={allAdmins}
                value={valueForRole(roleId)}
                onChange={(opts) => handleChangeRole(roleId, opts)}
                placeholder="Type to search users..."
                isDisabled={saving || !currentUserIsSuperAdmin}
                classNamePrefix="react-select"
              />
            </fieldset>
          </div>
        ))}
      </div>
    </div>
  );
}
